import { CANVAS_SETTINGS } from '../constants';
import { useEffect, useRef } from 'react';

export default function useAudioFileVisualization(audioFile?: Blob) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (canvasRef.current && audioFile) {
      const canvasCtx = canvasRef.current.getContext('2d');

      if (!canvasCtx) {
        throw new Error('Could not create 2D context');
      }

      //@ts-ignore
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const fileReader = new FileReader();

      fileReader.onloadend = () => {
        const arrayBuffer = fileReader.result as ArrayBuffer;

        audioContext.decodeAudioData(arrayBuffer, (audioBuffer) => {
          const channelData = audioBuffer.getChannelData(0);
          const samples = WIDTH;
          const blockSize = Math.floor(channelData.length / samples);
          let blockAverages: number[] = [];
          let xPos = 0;
          let yPos = HEIGHT / 2;

          const normalizeData = (data) => {
            const multiplier = Math.pow(Math.max(...data), -1);
            return data.map((n) => n * multiplier);
          };

          canvasCtx.beginPath();
          canvasCtx.moveTo(xPos, yPos);

          for (let i = 0; i < samples; i++) {
            let blockStart = blockSize * i;
            let sum = 0;

            for (let j = 0; j < blockSize; j++) {
              sum = sum + channelData[blockStart + j];
            }

            blockAverages.push(sum / blockSize);
          }

          const normalizedData = normalizeData(blockAverages);

          for (let i = 0; i < samples; i++) {
            yPos = normalizedData[i];
            xPos++;

            // Multiply by half the height to fill up the canvas
            // Add half the height to center the graph
            canvasCtx.lineTo(xPos, (yPos * HEIGHT) / 2 + HEIGHT / 2);
          }

          canvasCtx.stroke();
        });
      };

      const cssWidth = parseInt(getComputedStyle(canvasRef.current).getPropertyValue('width').slice(0, -2));
      const cssHeight = parseInt(getComputedStyle(canvasRef.current).getPropertyValue('height').slice(0, -2));

      const scale = window.devicePixelRatio;
      canvasRef.current.width = Math.floor(cssWidth * scale);
      canvasRef.current.height = Math.floor(cssHeight * scale);

      const WIDTH = canvasRef.current.width;
      const HEIGHT = canvasRef.current.height;

      canvasCtx.lineWidth = 2 * scale;
      canvasCtx.strokeStyle = CANVAS_SETTINGS.lineColor;

      //Load blob
      fileReader.readAsArrayBuffer(audioFile);
    }
  }, [audioFile]);

  const clearCanvas = () => {
    if (canvasRef.current) {
      const canvasCtx = canvasRef.current.getContext('2d');

      if (!canvasCtx) {
        throw new Error('Could not create 2D context');
      }

      canvasCtx.fillStyle = CANVAS_SETTINGS.backgroundColor;
      canvasCtx.fillRect(0, 0, canvasRef.current?.width!, canvasRef.current?.height!);
    }
  };

  return {
    canvasRef,
    clearCanvas
  };
}
